import { useEffect, useMemo, useRef } from "react";

import { ChevronLeftIcon } from "@vericus/cadmus-icons";
import { Button, ControlButton, Text } from "@vericus/cadmus-ui";

import { show } from "@ebay/nice-modal-react";
import { useJitsu } from "@jitsu/react";

import { ConnectionBar } from "@/components/connection-bar";
import { LoadingPage } from "@/components/loading-page";
import { JitsuEvent } from "@/data/events/JitsuEvent";
import { AbandonAttemptModal } from "@/features/socratic/components/abandon-attempt-modal";
import {
  AlreadyCompletedScreen,
  RecoveryScreen,
  SocraticDialogue,
} from "@/features/socratic/components/dialogue";
import { resolveDialogueTimeline } from "@/features/socratic/helpers/dialogue-timeline";
import { hasDialogueResponse } from "@/features/socratic/helpers/has-dialogue-response";
import { resolveStartFailure } from "@/features/socratic/helpers/start-failure";
import {
  useBackProtection,
  useReloadProtection,
  useStartDialogue,
} from "@/features/socratic/hooks";
import {
  ReattemptWindowExpiredError,
  SocraticPageError,
} from "@/features/socratic/socratic-page-error";
import { StartedDialogue } from "@/features/socratic/types";
import {
  AnswerBlockFragment,
  AssessmentFragment,
  SocraticDialogueFragment,
  UserFragment,
} from "@/generated/graphql";
import { CadmusLogo } from "@/ui/shared/CadmusLogo";
import { ManualWithMutations } from "@/ui/shared/ManualWithMutations";
import { getCurrentDate } from "@/utils/datetime";

import * as pageStyles from "./socratic.css";

interface Props {
  workId: string;
  /** Student owner of the work */
  student: UserFragment;
  /** Assessment the work belongs to */
  assessment: Pick<AssessmentFragment, "id" | "name">;
  answerBlock: AnswerBlockFragment;
  dialogue: SocraticDialogueFragment;
  onReattemptPressed: () => void;
  navigateToWork: () => void;
  numAttemptsRemaining: number;
}

/**
 * SocraticPage is the main layout component.
 *
 * It is responsible for starting the given Socratic Dialogue (or handling start errors),
 * and then passing this started Dialogue down to the SocraticDialogue component.
 */
export function SocraticPage(props: Props) {
  const {
    workId,
    student,
    assessment,
    answerBlock,
    dialogue,
    onReattemptPressed,
    navigateToWork,
    numAttemptsRemaining,
  } = props;

  const manualMountElement = useRef<HTMLDivElement>(null);
  const { track } = useJitsu();

  const {
    startDialogue,
    startedDialogue: startedSnapshot,
    isStartingDialogue,
    startError,
    apolloError,
  } = useStartDialogue();

  // Resolved from the dialogue as the start mutation returned it, not from the
  // live cache entity. Completing a turn writes the response back into the
  // cache, and re-reading it would swap a student's own retake view for the
  // completed screen and take their reattempt button away.
  const timeline = useMemo(
    () =>
      startedSnapshot != null
        ? resolveDialogueTimeline(startedSnapshot, getCurrentDate())
        : null,
    [startedSnapshot]
  );

  // Only an attempt the student can still act on is worth holding them on. The
  // recovery and completed screens are dead ends, and trapping someone there
  // behind a reload prompt gives them nothing to do.
  const isAttemptInProgress =
    timeline?.phase === "fresh" || timeline?.phase === "resume";

  // Read from the live entity, not the resolved phase: once the upload lands
  // there is nothing left to lose, and warning about a saved response would
  // offer to throw away work that is already stored.
  const shouldPreventUnload =
    isAttemptInProgress && !hasDialogueResponse(dialogue);

  useReloadProtection(shouldPreventUnload);

  useBackProtection(() => {
    if (shouldPreventUnload) {
      showAbandonAttemptModal();
    } else {
      navigateToWork();
    }
  });

  // Navigation
  const showAbandonAttemptModal = () => {
    show(AbandonAttemptModal, {
      onConfirm: () => {
        track(JitsuEvent.VIDEO_RESPONSE_ATTEMPT_FORFEITED, {
          dialogue_id: dialogue.id,
        });
        navigateToWork();
      },
      remainingAttempts: numAttemptsRemaining,
    });
  };
  const safeNavigateToWork = () => {
    if (shouldPreventUnload) {
      showAbandonAttemptModal();
    } else {
      navigateToWork();
    }
  };

  // Start the dialogue on load
  const hasStartedRef = useRef<boolean>(false);
  useEffect(() => {
    // prevent double-firing in dev (Strict Mode)
    if (hasStartedRef.current) {
      return;
    }
    hasStartedRef.current = true;
    startDialogue({
      dialogueId: dialogue.id,
      workId,
    });
  }, [dialogue.id, workId, startDialogue]);

  const startedDialogue: StartedDialogue | null =
    dialogue.startedAt != null ? (dialogue as StartedDialogue) : null;

  const isLoading = isStartingDialogue || !hasStartedRef.current;

  const startFailure = resolveStartFailure({
    startError,
    hasTransportError: apolloError != null,
  });

  const isUnknownError =
    startFailure === "unknown" || timeline == null || !startedDialogue;

  return (
    <>
      {/* Surfaces "Working Offline" so a student keeps the tab open while a
          recording upload waits to resume on reconnect. */}
      <ConnectionBar />
      {isLoading ? (
        <LoadingPage />
      ) : startFailure === "reattemptWindowExpired" ? (
        <ReattemptWindowExpiredError navigateToWork={navigateToWork} />
      ) : isUnknownError ? (
        <SocraticPageError navigateToWork={navigateToWork} />
      ) : (
        <div className={pageStyles.layout}>
          <Header
            assessmentName={assessment.name}
            studentName={student.name}
            manualMountElement={manualMountElement}
          />
          <PageNavigation
            onCancel={isAttemptInProgress ? safeNavigateToWork : navigateToWork}
            onComplete={
              isAttemptInProgress ? safeNavigateToWork : navigateToWork
            }
            hideCompleteButton={!isAttemptInProgress}
          />
          {timeline.phase === "expired" && (
            <RecoveryScreen
              answerBlock={answerBlock}
              otherDialogue={dialogue}
            />
          )}
          {timeline.phase === "completed" && (
            <AlreadyCompletedScreen
              answerBlock={answerBlock}
              completedDialogue={dialogue}
              navigateToWork={navigateToWork}
            />
          )}
          {isAttemptInProgress && (
            <SocraticDialogue
              workId={workId}
              startedDialogue={startedDialogue}
              timeline={timeline}
              answerBlock={answerBlock}
              onReattemptPressed={onReattemptPressed}
              numAttemptsRemaining={numAttemptsRemaining}
            />
          )}
        </div>
      )}
    </>
  );
}

function Header(props: {
  assessmentName: string;
  studentName: string;
  manualMountElement: React.RefObject<HTMLDivElement>;
}) {
  return (
    <div className={pageStyles.header}>
      <CadmusLogo />
      <Text
        kind="bodyLine"
        color="neutralgrey500"
        className={pageStyles.assessmentTitle}
      >
        {props.assessmentName} {"•"} {props.studentName}
      </Text>
      <ManualWithMutations
        mountElement={props.manualMountElement}
        trigger={
          <ControlButton iconName="Book" aria-label="Open Cadmus Manual" />
        }
      />
    </div>
  );
}

function PageNavigation(props: {
  onComplete?: () => void;
  onCancel: () => void;
  hideCompleteButton?: boolean;
}) {
  return (
    <div className={pageStyles.responseHeader}>
      <Button
        kind="outline"
        size="md"
        onClick={props.onCancel}
        icon={<ChevronLeftIcon label="" size={24} />}
      />
      <Text kind="displayThree" as="h1" className={pageStyles.responseTitle}>
        Record video response
      </Text>
      {props.hideCompleteButton !== true && (
        <Button onClick={props.onComplete} kind="secondary">
          Complete
        </Button>
      )}
    </div>
  );
}
